/** @file Handles the reset password form for new login and registration. */
import { AxiosError } from 'axios';
import moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import * as base64 from '@codegrade/base64';
import { maybe } from '@codegrade/utils';
import * as btn from '@/components/Button';
import * as form from '@/components/Form';
import PasswordSuggestions from '@/components/PasswordSuggestions';
import { PASSWORD_UNIQUE_MESSAGE } from '@/constants';
import * as utils from '@/utils';
import { clsInput } from './cls';
/** Form for setting a new password using a password reset link. */
export const ResetPassword = tsx.component({
    name: 'reset-password',
    props: {
        submit: p.ofType().required,
        userId: p.ofType().required,
        tenant: p.ofType().required,
        resetToken: p.ofType().required,
    },
    /** @returns State of the component. */
    data() {
        return {
            newPassword: '',
            confirmPassword: '',
            newLinkRequested: false,
        };
    },
    computed: {
        /**
         * Check whether the reset token has already expired.
         *
         * This assumes the token was serialized by a `URLSafeTimedSerializer`
         * from the `itsdangerous` Python package, which both truncates the
         * padding from the base64 encoded data and uses a nonstandard base64
         * alphabet.
         *
         * @see {@link https://docs.python.org/3/library/base64.html#base64.urlsafe_b64encode}
         * @returns Whether the token has expired.
         */
        hasTokenExpired() {
            if (this.resetToken.isNothing()) {
                return false;
            }
            try {
                const parts = this.resetToken.value.split('.');
                if (parts.length !== 3) {
                    return true;
                }
                const b64 = base64.utils.fromUrlsafe(base64.utils.fixPadding(parts[1]));
                // The timestamp is in seconds but `moment` wants milliseconds.
                const timestamp = moment(1000 * base64.getInt(b64));
                // Must be the same as `RESET_TOKEN_TIME` in `psef/models/user.py`.
                const maxAge = moment.duration(1, 'day');
                const now = this.$getRootInstance().$getNow(timestamp);
                return now.isAfter(timestamp.add(maxAge));
            }
            catch (e) {
                return false;
            }
        },
        /** @returns The form message for expired links. */
        expiredLinkMessage() {
            if (!this.hasTokenExpired) {
                return utils.emptyVNode();
            }
            const maybeData = maybe.and(this.tenant, this.userId);
            if (maybeData.isNothing()) {
                return utils.emptyVNode();
            }
            if (this.newLinkRequested) {
                return (<form.Message variant="box" semantic="actionable">
                        A new password reset link has been requested.
                    </form.Message>);
            }
            const [tenant, userId] = maybeData.value;
            return (<form.Message data-cy="reset-password-invalid-token" variant="box" semantic="highlighted">
                    <p class="title-small">It looks like this password reset link has expired.</p>
                    <p class="text-pretty">
                        You can request a new link by clicking the button below, which will
                        invalidate all other password reset links you have received so far.
                    </p>
                    <btn.PromiseButton semantic="highlighted" prominence={5} loadingAlertMessage="Requesting new link" successAlertMessage="A new link has been requested" action={async () => {
                    await this.$http.patch('/api/v1/login?type=reset_email', {
                        tenant_id: tenant.id,
                        user_id: userId,
                    });
                    return {
                        onAfterSuccess: () => {
                            this.newLinkRequested = true;
                        },
                    };
                }}>
                        Request a new link
                    </btn.PromiseButton>
                </form.Message>);
        },
        /** @returns The disabled reason of the submit button in this form. */
        submitDisabledReason() {
            if (this.newPassword.length === 0 ||
                this.confirmPassword.length === 0 ||
                this.newPassword !== this.confirmPassword) {
                return {
                    type: 'tooltip',
                    reason: 'Please fill both password inputs with the same password',
                };
            }
            return undefined;
        },
    },
    /** @returns The rendered reset password form JSX. */
    render() {
        return (<form.Root data-cy="reset-password-form" handleSubmit={async () => {
                await this.submit(this.newPassword);
            }} submitLoadingMessage="Resetting password..." submitSuccessMessage="Password reset" submitLabel="Reset password" submitDisabledReason={this.submitDisabledReason} submitOnEnter actionBarVariant="wide-submit" scopedSlots={{
                formError: err => {
                    if (err instanceof AxiosError) {
                        return api.WeakPasswordException.decode(err.response?.data).caseOf({
                            Left: () => utils.getErrorMessage(err),
                            Right: weakPwdException => (<PasswordSuggestions error={weakPwdException}/>),
                        });
                    }
                    return utils.getErrorMessage(err);
                },
            }}>
                {this.expiredLinkMessage}
                <form.Field label="New password">
                    <form.Description>{PASSWORD_UNIQUE_MESSAGE}</form.Description>
                    <form.Input class={clsInput} type="password" name="password" autocomplete="new-password" value={this.newPassword} onInput={val => {
                this.newPassword = val;
            }} required/>
                </form.Field>
                <form.Field label="Repeat password" validation={this.newPassword !== this.confirmPassword &&
                this.newPassword.length > 0 &&
                this.confirmPassword.length > 0
                ? 'Passwords do not match'
                : undefined}>
                    <form.Input class={clsInput} type="password" name="repeat-password" autocomplete="new-password" value={this.confirmPassword} onInput={val => {
                this.confirmPassword = val;
            }} required/>
                </form.Field>
            </form.Root>);
    },
});
